import { Role, ShiftStatus } from '@caresend/types';
import { computed } from 'vue';
import { shiftDetailsHeadingValues, shiftsListHeadingValues } from './components/shiftDetails.js';
import { PortalRouteName } from './model.js';
import { getStore } from '../../../store/helpers/initStore.js';

/* eslint-disable max-len */
const portalChildRoutes = [
    {
        path: '/itinerary/:itineraryID/'
            /** Parameters used to find a route and workflow item */
            + ':childRouteName?/:wfStoreRef?/:wfReferenceID?/'
            + ':wfItemID?/:wfContext?',
        name: PortalRouteName.ITINERARY,
        props: true,
        meta: {
            authRequired: true,
            minimizableNavBar: true,
            portal: {
                lockMap: 'collapsed',
                displayLegend: true,
                noPanelPadding: true,
                panelScrollLock: true,
            },
        },
        component: () => import('../../../components/specific/portal/components/Portal/components/ItineraryControlPanel/ItineraryControlPanel.vue.js'),
    },
    {
        path: '/job/:shiftID',
        name: PortalRouteName.JOB_DETAILS,
        props: true,
        meta: {
            authRequired: true,
            minimizableNavBar: true,
            portal: {
                lockMap: 'collapsed',
                noPanelPadding: true,
                panelScrollLock: true,
            },
            /**
             * If the shift is canceled, pass undefined instead of the heading values
             * so that the heading will not be rendered and we meet the design requirement
             */
            heading: (params) => {
                var _a, _b;
                const store = getStore();
                const { shiftID } = params.route.params;
                if (!shiftID)
                    throw new Error('Shift ID is missing when loading shift details');
                /**
                 * store.state.shifts.shifts should not be undefined according the type
                 * definition in this repo but in dashboard we have a separate type definition
                 * where it could be undefined
                 *
                 * we should unify these but for now I have used the optional operator here
                 */
                const shift = (_a = store.state.shifts.shifts) === null || _a === void 0 ? void 0 : _a[shiftID];
                const isNurseAndShiftCanceled = ((_b = store.state.auth.user) === null || _b === void 0 ? void 0 : _b.role) === Role.NURSE && (shift === null || shift === void 0 ? void 0 : shift.status) === ShiftStatus.CANCELED;
                if (isNurseAndShiftCanceled)
                    return computed(() => undefined);
                return computed(() => shiftDetailsHeadingValues(params).value);
            },
        },
        component: () => import('../../../components/specific/portal/components/Portal/components/Shifts/ShiftDetails.vue.js'),
    },
    {
        path: '/jobs',
        name: PortalRouteName.JOBS,
        meta: {
            authRequired: true,
            minimizableNavBar: true,
            portal: {
                noPanelPadding: true,
                panelScrollLock: true,
            },
            heading: shiftsListHeadingValues,
        },
        component: () => import('../../../components/specific/portal/components/Portal/components/Shifts/ShiftsList.vue.js'),
    },
    {
        path: '/orders',
        name: PortalRouteName.ORDERS,
        meta: {
            authRequired: true,
            minimizableNavBar: true,
            portal: {
                noPanelPadding: true,
                panelScrollLock: true,
            },
        },
        beforeEnter: (_to, _from, next) => {
            var _a;
            return ((_a = getStore().state.auth.user) === null || _a === void 0 ? void 0 : _a.role) !== Role.NURSE
                ? next()
                : next(false);
        },
        component: () => import('../../../components/specific/portal/components/Portal/components/OrdersList/OrdersList.vue.js'),
    },
    {
        path: '/procedure/:procedureID/:view?',
        name: PortalRouteName.PROCEDURE,
        props: true,
        meta: {
            authRequired: true,
            minimizableNavBar: true,
            portal: {
                lockMap: 'full',
                noPanelPadding: true,
                panelScrollLock: true,
            },
        },
        component: () => import('../../../components/specific/ProcedureDetails/ProcedureDetails/PortalProcedurePage.vue.js'),
    },
    {
        path: '/patient-dob/:draftWaypointID',
        name: PortalRouteName.SHIFT_CANCELATION_PATIENT_DOB,
        props: true,
        meta: {
            noPortal: true,
            authRequired: true,
            minimizableNavBar: true,
            portal: {
                lockMap: 'collapsed',
                noPanelPadding: true,
                panelScrollLock: true,
            },
        },
        component: () => import('../../../components/specific/portal/components/Portal/components/Shifts/components/ShiftCancelation/ShiftCancelationPatientDOB.vue.js'),
    },
];
const getPortalChildRoutes = () => portalChildRoutes;

export { getPortalChildRoutes };
